import { useState, useEffect, useCallback } from "react";
import { api } from "../../App";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Search, Check, X, Loader2, MapPin, FileText, Image as ImageIcon, ExternalLink, User as UserIcon, Mail, Save } from "lucide-react";
import { toast } from "sonner";
import SearchableSelect from "../../components/SearchableSelect";
import {
  getBrandNames,
  getModelsForBrand,
  getAllowedYears,
  VEHICLE_COLORS_ES,
} from "../../data/vehicleBrandsMexico";

/**
 * Admin Vehicles tab with a full review-detail modal.
 *
 * Replaces the legacy inline table that only had Approve/Reject icon buttons.
 * Clicking a row now opens a Sheet/Modal showing EVERY field the host
 * submitted — license plates, VIN, registration document, insurance
 * (Multi-plataforma), photos, deposit, daily rate, location, status,
 * submission date — plus the host's name/email so admins can follow up.
 * Approve/Decline buttons live inside the modal; declining requires a
 * reason which is included in the host notification email.
 *
 * Backend endpoints used (already exist):
 *   - GET    /api/admin/vehicles
 *   - GET    /api/vehicles/{id}       — full vehicle doc (auth-aware)
 *   - PUT    /api/admin/vehicles/{id}/status   { status, reason? }
 */
export default function AdminVehiclesTab() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [declineReason, setDeclineReason] = useState("");

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vehicles");
      setVehicles(data?.items || data?.vehicles || (Array.isArray(data) ? data : []));
    } catch (_e) {
      toast.error("No pudimos cargar los vehículos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const openDetail = async (vehicle) => {
    setSelected(vehicle);
    setDetail(null);
    setDeclineReason("");
    setDetailLoading(true);
    try {
      // The admin vehicle list already contains most fields, but we fetch
      // the full doc so we get insurance docs / registration / VIN that
      // are projected away from the list response.
      const { data } = await api.get(`/admin/vehicles/${vehicle.vehicle_id}`);
      setDetail(data);
    } catch (_e) {
      // Fallback to the row data — at least the admin sees what they had.
      setDetail(vehicle);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    if (!selected) return;
    if (status === "rejected" && !declineReason.trim()) {
      toast.error("Por favor escribe una razón para rechazar");
      return;
    }
    setActing(true);
    try {
      await api.put(`/admin/vehicles/${selected.vehicle_id}/status`, {
        status,
        reason: status === "rejected" ? declineReason.trim() : undefined,
      });
      toast.success(status === "approved" ? "Vehículo aprobado y anfitrión notificado" : "Vehículo rechazado y anfitrión notificado");
      setSelected(null);
      await fetchVehicles();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos actualizar el estado");
    } finally {
      setActing(false);
    }
  };

  const filtered = vehicles.filter((v) => {
    const matchesSearch = `${v.make} ${v.model} ${v.host?.name || ""}`.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === "all" || v.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="card-base" data-testid="admin-vehicles-tab">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar vehículo o anfitrión..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="vehicles-search-input"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-base h-9 text-sm w-auto"
          data-testid="vehicles-filter-status"
        >
          <option value="all">Todos los estados</option>
          <option value="pending">Pendientes</option>
          <option value="approved">Aprobados</option>
          <option value="rejected">Rechazados</option>
        </select>
      </div>

      {loading ? (
        <div className="py-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-neutral-500">No hay vehículos.</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Vehículo</TableHead>
                <TableHead>Anfitrión</TableHead>
                <TableHead>Precio/día</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acción</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((v) => (
                <TableRow
                  key={v.vehicle_id}
                  className="cursor-pointer hover:bg-neutral-50"
                  onClick={() => openDetail(v)}
                  data-testid={`vehicle-row-${v.vehicle_id}`}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <img
                        src={v.photos?.[0] || "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=50"}
                        alt=""
                        className="w-12 h-12 object-cover rounded-lg shrink-0"
                      />
                      <span className="font-medium truncate">
                        {v.make} {v.model} {v.year}
                      </span>
                      {v.custom_model && (
                        <Badge
                          className="bg-purple-100 text-purple-700 shrink-0"
                          data-testid={`custom-model-badge-${v.vehicle_id}`}
                        >
                          Modelo personalizado
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">{v.host?.name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap">${v.daily_rate?.toLocaleString()}</TableCell>
                  <TableCell>
                    <Badge
                      className={
                        v.status === "approved"
                          ? "bg-green-100 text-green-700"
                          : v.status === "pending"
                          ? "bg-yellow-100 text-yellow-700"
                          : "bg-red-100 text-red-700"
                      }
                    >
                      {v.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); openDetail(v); }}
                      data-testid={`vehicle-view-${v.vehicle_id}`}
                    >
                      Ver detalles
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail modal */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="vehicle-detail-modal">
          <DialogHeader>
            <DialogTitle>
              {selected ? `${selected.make} ${selected.model} ${selected.year || ""}` : "Vehículo"}
              {selected?.custom_model && (
                <Badge className="bg-purple-100 text-purple-700 ml-2 align-middle">
                  Modelo personalizado — revisar
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {detailLoading ? (
            <div className="py-10 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-neutral-400" />
            </div>
          ) : detail ? (
            <div className="space-y-6 text-sm">
              {/* Photos */}
              {Array.isArray(detail.photos) && detail.photos.length > 0 && (
                <div>
                  <p className="label-overline mb-2">Fotos ({detail.photos.length})</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {detail.photos.map((p, i) => (
                      <a key={i} href={p} target="_blank" rel="noopener noreferrer">
                        <img src={p} alt="" className="w-full h-28 object-cover rounded-lg hover:opacity-90 transition" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Host */}
              <Section title="Anfitrión" icon={UserIcon}>
                <KV k="Nombre" v={detail.host?.name} />
                <KV k="Email" v={detail.host?.email} mono />
                <KV k="Teléfono" v={detail.host?.phone} mono />
                <KV k="Host ID" v={detail.host_id} mono />
              </Section>

              {/* Vehicle — editable canonical fields (make/model/year/color)
                  so admin ops can normalise legacy free-text listings. */}
              <Section title="Vehículo" icon={FileText}>
                <VehicleInfoEditor
                  detail={detail}
                  onSaved={(patch) => setDetail({ ...detail, ...patch })}
                />
                <KV k="Placas" v={detail.plate_number || detail.license_plate} mono />
                <KV k="VIN" v={detail.vin} mono />
                <KV k="Transmisión" v={detail.transmission} />
                <KV k="Combustible" v={detail.fuel_type} />
                <KV k="Asientos" v={detail.seats} />
                <KV k="Vehicle ID" v={detail.vehicle_id} mono />
              </Section>

              {/* Pricing & deposit */}
              <Section title="Precios y depósito" icon={FileText}>
                <KV k="Precio diario" v={detail.daily_rate ? `$${detail.daily_rate.toLocaleString()} MXN` : "—"} />
                <KV k="Tarifa de entrega" v={detail.delivery_fee ? `$${detail.delivery_fee.toLocaleString()} MXN` : "—"} />
                <KV k="Descuento semanal" v={detail.weekly_discount ? `${detail.weekly_discount}%` : "—"} />
                <KV k="Descuento mensual" v={detail.monthly_discount ? `${detail.monthly_discount}%` : "—"} />
                <KV k="Reserva instantánea" v={detail.instant_book ? "Sí" : "No"} />
              </Section>

              {/* Location */}
              <Section title="Ubicación" icon={MapPin}>
                <KV k="Dirección" v={detail.location_address} />
                <KV k="Ciudad" v={detail.location_city} />
                <KV k="Coordenadas" v={detail.location_lat && detail.location_lng ? `${detail.location_lat}, ${detail.location_lng}` : "—"} mono />
              </Section>

              {/* Documents */}
              <Section title="Documentos" icon={FileText}>
                <DocLink label="Tarjeta de circulación / Registro" url={detail.registration_document_url} />
                <DocLink label="Seguro Multi-plataforma" url={detail.insurance_document_url} />
                <DocLink label="Verificación vehicular" url={detail.verification_document_url} />
                {Array.isArray(detail.additional_documents) && detail.additional_documents.length > 0 && (
                  <div className="text-xs text-neutral-500">+ {detail.additional_documents.length} documentos adicionales</div>
                )}
              </Section>

              {/* Status & timestamps */}
              <Section title="Estado" icon={FileText}>
                <KV k="Estado" v={detail.status} />
                <KV k="Enviado el" v={fmtDate(detail.created_at)} />
                <KV k="Última actualización" v={fmtDate(detail.updated_at)} />
                {detail.rejection_reason && <KV k="Razón de rechazo" v={detail.rejection_reason} />}
              </Section>

              {/* Actions */}
              {detail.status === "pending" ? (
                <div className="border-t pt-4 space-y-3">
                  <CategorySelect
                    value={detail.category || ""}
                    onChange={async (newCat) => {
                      try {
                        await api.put(`/vehicles/${selected.vehicle_id}`, { category: newCat });
                        setDetail({ ...detail, category: newCat });
                        toast.success("Categoría actualizada");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "No pudimos actualizar la categoría");
                      }
                    }}
                  />
                  <Textarea
                    placeholder="Razón (requerida si rechazas — se envía al anfitrión)"
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    rows={2}
                    className="resize-none"
                    data-testid="vehicle-decline-reason"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleStatusChange("approved")}
                      disabled={acting}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      data-testid="vehicle-approve-btn"
                    >
                      {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                      Aprobar
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleStatusChange("rejected")}
                      disabled={acting || !declineReason.trim()}
                      className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                      data-testid="vehicle-reject-btn"
                    >
                      {acting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <X className="w-4 h-4 mr-2" />}
                      Rechazar
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="border-t pt-4 text-xs text-neutral-500">
                  Este vehículo ya fue revisado. Solo los vehículos pendientes pueden aprobarse o rechazarse.
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div>
      <p className="label-overline mb-2 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5" />}
        {title}
      </p>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function KV({ k, v, mono = false }) {
  if (v === null || v === undefined || v === "") return null;
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs text-neutral-500 w-40 shrink-0">{k}</span>
      <span className={`text-sm ${mono ? "font-mono" : ""} text-neutral-800 break-all`}>{v}</span>
    </div>
  );
}

function DocLink({ label, url }) {  if (!url) {
    return (
      <div className="flex items-baseline gap-3">
        <span className="text-xs text-neutral-500 w-40 shrink-0">{label}</span>
        <span className="text-sm text-neutral-400">— no subido</span>
      </div>
    );
  }
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs text-neutral-500 w-40 shrink-0">{label}</span>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-sm text-terracotta hover:underline inline-flex items-center gap-1"
      >
        Abrir documento
        <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = typeof iso === "string" ? new Date(iso) : iso;
    return d.toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (_e) {
    return iso;
  }
}

// Category dropdown — same enum as the backend `VEHICLE_CATEGORY_SET`.
// Admin can pick during approval; choice determines the homepage carousel
// where this vehicle appears.
const ADMIN_CATEGORIES = [
  { id: "truck",    label: "Camioneta / Pickup" },
  { id: "suv",      label: "SUV" },
  { id: "compact",  label: "Compacto / Hatchback" },
  { id: "economy",  label: "Económico" },
  { id: "electric", label: "Eléctrico" },
  { id: "luxury",   label: "De lujo" },
  { id: "van",      label: "Van / Familiar" },
  { id: "offroad",  label: "Off-road / 4x4" },
  { id: "sports",   label: "Deportivo" },
];

function CategorySelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-3" data-testid="vehicle-category-select-wrap">
      <span className="text-xs text-neutral-500 w-40 shrink-0">Categoría del marketplace</span>
      <select
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="input-base h-9 text-sm flex-1"
        data-testid="vehicle-category-select"
      >
        <option value="">— sin categoría (se infiere) —</option>
        {ADMIN_CATEGORIES.map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * VehicleInfoEditor — inline editable Marca / Modelo / Año / Color for
 * the admin review modal. Uses the same catalog as the host onboarding
 * form so admin ops can normalise legacy free-text listings without
 * bouncing back through the host.
 */
function VehicleInfoEditor({ detail, onSaved }) {
  const [make, setMake] = useState(detail?.make || "");
  const [model, setModel] = useState(detail?.model || "");
  const [year, setYear] = useState(detail?.year ? String(detail.year) : "");
  const [color, setColor] = useState(detail?.color || "");
  const [saving, setSaving] = useState(false);

  // Re-sync local state whenever admin selects a different vehicle.
  useEffect(() => {
    setMake(detail?.make || "");
    setModel(detail?.model || "");
    setYear(detail?.year ? String(detail.year) : "");
    setColor(detail?.color || "");
  }, [detail?.vehicle_id, detail?.make, detail?.model, detail?.year, detail?.color]);

  const dirty =
    (make || "") !== (detail?.make || "") ||
    (model || "") !== (detail?.model || "") ||
    String(year || "") !== String(detail?.year || "") ||
    (color || "") !== (detail?.color || "");

  const save = async () => {
    if (!make.trim()) return toast.error("Selecciona la marca");
    if (!model.trim()) return toast.error("Selecciona el modelo");
    const yr = parseInt(year, 10);
    const allowed = getAllowedYears();
    const minY = allowed[allowed.length - 1];
    const maxY = allowed[0];
    if (!yr || yr < minY || yr > maxY)
      return toast.error(`El año debe estar entre ${minY} y ${maxY}`);
    if (!color.trim()) return toast.error("Selecciona el color");

    setSaving(true);
    try {
      const res = await api.patch(`/admin/vehicles/${detail.vehicle_id}/info`, {
        make: make.trim(),
        model: model.trim(),
        year: yr,
        color: color.trim(),
      });
      toast.success("Información actualizada");
      onSaved?.(res.data?.vehicle || { make, model, year: yr, color });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No pudimos guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 pb-1" data-testid="admin-vehicle-info-editor">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Marca</label>
          <SearchableSelect
            value={make}
            onChange={(v) => { setMake(v); setModel(""); }}
            options={getBrandNames()}
            placeholder="Selecciona marca"
            searchPlaceholder="Buscar marca..."
            testId="admin-vehicle-make"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Modelo</label>
          <SearchableSelect
            value={model}
            onChange={setModel}
            options={getModelsForBrand(make)}
            placeholder="Selecciona modelo"
            searchPlaceholder="Buscar modelo..."
            disabled={!make}
            disabledMessage="Selecciona una marca primero"
            testId="admin-vehicle-model"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Año</label>
          <SearchableSelect
            value={year}
            onChange={setYear}
            options={getAllowedYears().map((y) => String(y))}
            placeholder="Selecciona año"
            searchPlaceholder="Buscar año..."
            testId="admin-vehicle-year"
          />
        </div>
        <div>
          <label className="text-xs text-neutral-500 block mb-1">Color</label>
          <SearchableSelect
            value={color}
            onChange={setColor}
            options={VEHICLE_COLORS_ES}
            placeholder="Selecciona color"
            searchPlaceholder="Buscar color..."
            testId="admin-vehicle-color"
          />
        </div>
      </div>
      <div className="flex justify-end">
        <Button
          size="sm"
          onClick={save}
          disabled={!dirty || saving}
          className="h-8 text-xs"
          data-testid="admin-vehicle-info-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Guardar
        </Button>
      </div>
    </div>
  );
}
